import { AxiosInstance, AxiosResponse } from 'axios';

export const getCompany = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<Company>> => {
  const response: AxiosResponse<ApiResponse<Company>> = await axiosInstance.get(
    '/company'
  );
  return response.data;
};

export const getCompanyName = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<string>> => {
  const response: AxiosResponse<ApiResponse<string>> = await axiosInstance.get(
    '/company/name'
  );
  return response.data;
};

export const getCodes = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CompanyCode>> => {
  const response: AxiosResponse<ApiResponse<CompanyCode>> =
    await axiosInstance.get('/company/codes');
  return response.data;
};

export const getEmployees = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<PublicEmployeeProfile[]>> => {
  const response: AxiosResponse<ApiResponse<PublicEmployeeProfile[]>> =
    await axiosInstance.get('/company/employees');
  return response.data;
};

export const getBirthdays = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<BirthdayPermission[]>> => {
  const response: AxiosResponse<ApiResponse<BirthdayPermission[]>> =
    await axiosInstance.get('/company/birthdays');
  return response.data;
};

export const getConfig = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CompanyConfig[]>> => {
  const response: AxiosResponse<ApiResponse<CompanyConfig[]>> =
    await axiosInstance.get('/company/config');
  return response.data;
};

export const getPtoRequests = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<PtoRequest[]>> => {
  const response: AxiosResponse<ApiResponse<PtoRequest[]>> =
    await axiosInstance.get('/company/pto_requests');
  return response.data;
};

export const updateConfig = async (
  axiosInstance: AxiosInstance,
  config: CompanyConfig
): Promise<ApiResponse<CompanyConfig>> => {
  const response: AxiosResponse<ApiResponse<CompanyConfig>> =
    await axiosInstance.post('/company/config', config);
  return response.data;
};

export const getLogo = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<string | null>> => {
  const response: AxiosResponse<ApiResponse<string | null>> =
    await axiosInstance.get('/company/logo');
  return response.data;
};

export const uploadLogo = async (
  axiosInstance: AxiosInstance,
  formData: FormData
): Promise<ApiResponse<string | null>> => {
  const response: AxiosResponse<ApiResponse<string | null>> =
    await axiosInstance.post(`/company/upload_logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

  return response.data;
};

export const getActivePrismUsers = async (
  axiosInstance: AxiosInstance,
  prismUserType?: readablePrismUserTypes
): Promise<ApiResponse<PrismSecurityUser[]>> => {
  const response: AxiosResponse<ApiResponse<PrismSecurityUser[]>> =
    await axiosInstance.get('/company/active-prism-users', {
      params: { user_type: prismUserType },
    });
  return response.data;
};

export const getLaborNotice = async (
  axiosInstance: AxiosInstance,
  filename: string
): Promise<Blob> => {
  const response = await axiosInstance.get('/company/labor_notice', {
    responseType: 'blob',
    params: { filename },
  });
  return response.data;
};

// ---------------------------------------------------------------------------
// Handbook CRUD
// ---------------------------------------------------------------------------

export const getCompanyHandbooks = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<HandbookAsset[]>> => {
  const response: AxiosResponse<ApiResponse<HandbookAsset[]>> =
    await axiosInstance.get('/company/handbooks');
  return response.data;
};

export const uploadHandbook = async (
  axiosInstance: AxiosInstance,
  formData: FormData
): Promise<ApiResponse<any>> => {
  const response = await axiosInstance.post('/company/handbooks', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteHandbook = async (
  axiosInstance: AxiosInstance,
  handbook_id: number
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.delete(`/company/handbooks/${handbook_id}`);
  return response.data;
};

export const setHandbookAutoReminder = async (
  axiosInstance: AxiosInstance,
  handbook: HandbookAsset,
  config: Record<string, unknown> | null
): Promise<ApiResponse<HandbookAsset>> => {
  const response: AxiosResponse<ApiResponse<HandbookAsset>> =
    await axiosInstance.put(`/company/handbooks/${handbook.id}`, {
      client_id: handbook.client_id,
      asset_type: handbook.asset_type,
      archived: handbook.archived,
      config,
    });
  return response.data;
};

export const sendHandbookReminder = async (
  axiosInstance: AxiosInstance,
  remindeeId: string,
  handbookId: number
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.post(
      `/company/handbooks/reminder?remindee_id=${remindeeId}&handbook_id=${handbookId}`
    );
  return response.data;
};

export const archiveEmployeeHandbook = async (
  axiosInstance: AxiosInstance,
  handbook_id: number,
  payload: HandbookAsset
): Promise<ApiResponse<HandbookAsset>> => {
  const response: AxiosResponse<ApiResponse<HandbookAsset>> =
    await axiosInstance.put(`/company/handbooks/${handbook_id}/archive`, {
      client_id: payload.client_id,
      asset_type: payload.asset_type,
      archived: payload.archived,
      config: payload.config,
    });
  return response.data;
};

export const assignEmployeeHandbook = async (
  axiosInstance: AxiosInstance,
  payload: EmployeeHandbookAssignment[]
): Promise<ApiResponse<EmployeeHandbookAssignment[]>> => {
  const response: AxiosResponse<ApiResponse<EmployeeHandbookAssignment[]>> =
    await axiosInstance.post('/company/handbooks/assign', payload);
  return response.data;
};

export const getEmployeeAssignments = async (
  axiosInstance: AxiosInstance,
  handbook_id: number
): Promise<ApiResponse<EmployeeHandbookAssignment[]>> => {
  const response: AxiosResponse<ApiResponse<EmployeeHandbookAssignment[]>> =
    await axiosInstance.get(`/company/handbooks/assign/${handbook_id}`);
  return response.data;
};

export const updateAssignmentDeadline = async (
  axiosInstance: AxiosInstance,
  assignmentId: number,
  payload: { due_date: string | null }
): Promise<ApiResponse<EmployeeHandbookAssignment>> => {
  const response: AxiosResponse<ApiResponse<EmployeeHandbookAssignment>> =
    await axiosInstance.patch(
      `/company/handbooks/assign/${assignmentId}`,
      payload
    );
  return response.data;
};

export const unassignEmployeeHandbook = async (
  axiosInstance: AxiosInstance,
  payload: number[]
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.post('/company/handbooks/unassign', payload);
  return response.data;
};

export const downloadSignedHandbook = async (
  axiosInstance: AxiosInstance,
  assignmentId: number
): Promise<Blob> => {
  const response = await axiosInstance.get(
    `/company/handbooks/assignments/${assignmentId}/signed-pdf`,
    { responseType: 'blob' }
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Company Holiday CRUD
// ---------------------------------------------------------------------------

export const getCompanyHolidays = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CompanyHoliday[]>> => {
  const response: AxiosResponse<ApiResponse<CompanyHoliday[]>> =
    await axiosInstance.get('/company/holidays');
  return response.data;
};

export const getComputedCompanyHolidays = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<Holiday[]>> => {
  const response: AxiosResponse<ApiResponse<Holiday[]>> =
    await axiosInstance.get('/company/holidays-computed');
  return response.data;
};

export const createCompanyHoliday = async (
  axiosInstance: AxiosInstance,
  payload: CompanyHolidayForm
): Promise<ApiResponse<CompanyHoliday>> => {
  const response: AxiosResponse<ApiResponse<CompanyHoliday>> =
    await axiosInstance.post('/company/holidays', payload);
  return response.data;
};

export const updateCompanyHoliday = async (
  axiosInstance: AxiosInstance,
  holidayId: number,
  payload: CompanyHolidayForm
): Promise<ApiResponse<CompanyHoliday>> => {
  const response: AxiosResponse<ApiResponse<CompanyHoliday>> =
    await axiosInstance.put(`/company/holidays/${holidayId}`, payload);
  return response.data;
};

export const deleteCompanyHoliday = async (
  axiosInstance: AxiosInstance,
  holidayId: number
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.delete(`/company/holidays/${holidayId}`);
  return response.data;
};

export const getCsaAcknowledgement = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CsaAcknowledgement | null>> => {
  const response: AxiosResponse<ApiResponse<CsaAcknowledgement | null>> =
    await axiosInstance.get('/company/csa-acknowledgement');
  return response.data;
};

export const acknowledgeCsa = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CsaAcknowledgement>> => {
  const response: AxiosResponse<ApiResponse<CsaAcknowledgement>> =
    await axiosInstance.put('/company/csa-acknowledgement/acknowledge');
  return response.data;
};

export const downloadCsaTerms = async (
  axiosInstance: AxiosInstance
): Promise<Blob> => {
  const response = await axiosInstance.get(
    '/company/csa-acknowledgement/download',
    { responseType: 'blob' }
  );
  return response.data;
};

export const companyService = {
  getCodes,
  getCompany,
  getCompanyName,
  getEmployees,
  getBirthdays,
  getConfig,
  getLogo,
  getPtoRequests,
  updateConfig,
  uploadLogo,
  getActivePrismUsers,
  getLaborNotice,
  getCompanyHandbooks,
  uploadHandbook,
  deleteHandbook,
  archiveEmployeeHandbook,
  setHandbookAutoReminder,
  sendHandbookReminder,
  assignEmployeeHandbook,
  getEmployeeAssignments,
  updateAssignmentDeadline,
  unassignEmployeeHandbook,
  downloadSignedHandbook,
  getCompanyHolidays,
  getComputedCompanyHolidays,
  createCompanyHoliday,
  updateCompanyHoliday,
  deleteCompanyHoliday,
  getCsaAcknowledgement,
  acknowledgeCsa,
  downloadCsaTerms,
};
